import { useState, type ChangeEvent } from 'react';
import { Input, Label, Textarea } from '@monksflow/monks-ui';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { type Tracker } from '@/entities/tracker';
import {
  TEMPLATE_VISIBILITIES,
  useSaveTrackerTemplateMutation,
  type TrackerTemplateVisibility,
} from '@/entities/tracker-template';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { boardTypeOf } from '@/widgets/trackers-board';
import { track } from '@/shared/analytics';
import { notificationService } from '@/lib/services/notification.service';
import { FormDialog } from '@/shared/ui/FormDialog';

/**
 * Snapshots a tracker's structure as a reusable template, which also takes the
 * tracker's icon and colour. Its fields are seeded on mount, so mount it per
 * opening (`useModalState`) to start from the tracker's current name.
 */
export function SaveTrackerTemplateDialog({
  tracker,
  open,
  onClose,
}: {
  tracker: Tracker;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation('trackers');
  const workspaceId = useCurrentWorkspaceId() ?? '';
  const saveTemplate = useSaveTrackerTemplateMutation({ workspaceId });

  // eslint-disable-next-line i18n-freeze/no-frozen-translation -- seeds a value the user then edits
  const [tplName, setTplName] = useState<string>(
    t('trackers.trackerDetailsMenu.templateName', { name: tracker.name }),
  );
  const [tplDescription, setTplDescription] = useState(tracker.description ?? '');
  const [visibility, setVisibility] = useState<TrackerTemplateVisibility>('workspace');

  const handleSaveSubmit = () => {
    const trimmed = tplName.trim();
    if (!trimmed) return;
    saveTemplate.mutate(
      {
        sourceTrackerId: tracker.id,
        name: trimmed,
        description: tplDescription.trim() || undefined,
        visibility,
      },
      {
        onSuccess: () => {
          onClose();
          track('tracker_template_saved', {
            board_type: boardTypeOf(tracker.itemSource),
            visibility,
          });
          notificationService.showSuccess(t('trackers.trackerDetailsMenu.templateSaved'));
        },
        onError: () =>
          notificationService.showError(t('trackers.trackerDetailsMenu.couldNotSaveTheTemplate')),
      },
    );
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('trackers.trackerDetailsMenu.saveAsTemplate')}
    >
      <FormDialog.Body>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="template-name" className="text-foreground">
              {t('trackers.trackerDetailsMenu.name')}
            </Label>
            <Input
              id="template-name"
              value={tplName}
              maxLength={120}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setTplName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveSubmit();
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1">
              <Label htmlFor="template-description" className="text-foreground">
                {t('trackers.trackerDetailsMenu.description')}
              </Label>
              <span className="text-sm font-normal text-muted-foreground">
                {t('trackers.trackerDetailsMenu.optional')}
              </span>
            </div>
            <Textarea
              id="template-description"
              value={tplDescription}
              maxLength={500}
              rows={2}
              onChange={(e) => setTplDescription(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground">
              {t('trackers.trackerDetailsMenu.whoCanReuseIt')}
            </span>
            <div className="flex flex-col gap-2">
              {TEMPLATE_VISIBILITIES.map((v) => {
                const selected = visibility === v.value;
                return (
                  <button
                    key={v.value}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setVisibility(v.value)}
                    className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      selected
                        ? 'border-primary bg-accent'
                        : 'border-border bg-background hover:border-primary hover:bg-accent'
                    }`}
                  >
                    <span className="flex flex-1 flex-col gap-0.5">
                      <span className="text-sm font-medium text-foreground">{v.label}</span>
                      <span className="text-xs text-muted-foreground">{v.description}</span>
                    </span>
                    {selected ? (
                      <Check className="size-4 shrink-0 text-foreground" aria-hidden="true" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </FormDialog.Body>
      <FormDialog.Footer
        primary={{
          label: t('trackers.trackerDetailsMenu.saveTemplate'),
          onClick: handleSaveSubmit,
          disabled: tplName.trim().length === 0,
          loading: saveTemplate.isPending,
        }}
        secondary={{
          label: t('trackers.trackerDetailsMenu.cancel'),
          onClick: onClose,
        }}
      />
    </FormDialog>
  );
}
